/**
 * Module Context - React context for module state management.
 *
 * Provides a React-friendly way to access and respond to module state changes.
 *
 * @example
 * ```tsx
 * // In your app root
 * <ModuleProvider>
 *   <App />
 * </ModuleProvider>
 *
 * // In components
 * const { isModuleEnabled, getNavItems } = useModules();
 * if (isModuleEnabled('exam')) {
 *   // Render exam features
 * }
 * ```
 */

import type React from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  ModuleCode,
  ModuleState,
  ModuleContextValue,
  NavItem,
  RouteConfig,
  DashboardWidgetConfig,
  QuickActionConfig,
} from './types';
import * as registry from './moduleRegistry';

/**
 * React context for module state.
 */
const ModuleContext = createContext<ModuleContextValue | undefined>(undefined);

/**
 * Props for the ModuleProvider component.
 */
interface ModuleProviderProps {
  /**
   * Child components.
   */
  children: ReactNode;

  /**
   * API endpoint for fetching module configuration.
   * If provided, module states will be synced with the backend on mount.
   */
  apiEndpoint?: string;

  /**
   * Initial feature flags to set.
   */
  initialFeatureFlags?: Record<string, boolean>;
}

/**
 * Provider component for module context.
 * Manages module state and provides it to child components.
 *
 * @example
 * ```tsx
 * <ModuleProvider
 *   apiEndpoint="/api/modules"
 *   initialFeatureFlags={{ 'ai-analytics': true }}
 * >
 *   <App />
 * </ModuleProvider>
 * ```
 */
export function ModuleProvider({
  children,
  apiEndpoint,
  initialFeatureFlags,
}: ModuleProviderProps): React.JSX.Element {
  const [modules, setModules] = useState<Map<ModuleCode, ModuleState>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(!!apiEndpoint);
  const [error, setError] = useState<string | undefined>();

  // Initialize feature flags
  useEffect(() => {
    if (initialFeatureFlags) {
      registry.setFeatureFlags(initialFeatureFlags);
    }
  }, [initialFeatureFlags]);

  // Subscribe to registry changes
  useEffect(() => {
    const unsubscribe = registry.subscribeToChanges((newModules) => {
      setModules(new Map(newModules));
    });

    // Initialize with current state
    setModules(
      new Map(
        registry.getAllModules().map((m) => [m.config.code, m])
      )
    );

    return unsubscribe;
  }, []);

  // Sync with backend if endpoint provided
  useEffect(() => {
    if (!apiEndpoint) return;

    const syncModules = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(apiEndpoint);
        if (!response.ok) {
          throw new Error(`Failed to fetch module configuration: ${response.statusText}`);
        }
        const data = await response.json();
        // Extract modules array from API response (ApiResponseDto<ModuleConfigurationDto>)
        const modules = data?.data?.modules || [];
        await registry.syncWithBackend(modules);
        setError(undefined);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to sync modules');
        console.error('Failed to sync modules with backend:', err);
      } finally {
        setIsLoading(false);
      }
    };

    syncModules();
  }, [apiEndpoint]);

  // Memoized context methods
  const isModuleEnabled = useCallback(
    (code: ModuleCode): boolean => registry.isModuleEnabled(code),
    []
  );

  const getEnabledModules = useCallback(
    (): ModuleState[] => registry.getEnabledModules(),
    []
  );

  const getNavItems = useCallback(
    (): NavItem[] => registry.getNavItems(),
    []
  );

  const getRoutes = useCallback(
    (): RouteConfig[] => registry.getRoutes(),
    []
  );

  const getDashboardWidgets = useCallback(
    (): DashboardWidgetConfig[] => registry.getDashboardWidgets(),
    []
  );

  const getQuickActions = useCallback(
    (): QuickActionConfig[] => registry.getQuickActions(),
    []
  );

  const isFeatureEnabled = useCallback(
    (flag: string): boolean => registry.isFeatureEnabled(flag),
    []
  );

  // Build context value
  const contextValue = useMemo<ModuleContextValue>(
    () => ({
      modules,
      isModuleEnabled,
      getEnabledModules,
      getNavItems,
      getRoutes,
      getDashboardWidgets,
      getQuickActions,
      isFeatureEnabled,
      isLoading,
      error,
    }),
    [
      modules,
      isModuleEnabled,
      getEnabledModules,
      getNavItems,
      getRoutes,
      getDashboardWidgets,
      getQuickActions,
      isFeatureEnabled,
      isLoading,
      error,
    ]
  );

  return (
    <ModuleContext.Provider value={contextValue}>
      {children}
    </ModuleContext.Provider>
  );
}

/**
 * Hook to access module context.
 *
 * @returns Module context value
 * @throws Error if used outside of ModuleProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isModuleEnabled, getNavItems } = useModules();
 *
 *   if (!isModuleEnabled('exam')) {
 *     return null;
 *   }
 *
 *   return <Navigation items={getNavItems()} />;
 * }
 * ```
 */
export function useModules(): ModuleContextValue {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
}

/**
 * Hook to check if a specific module is enabled.
 *
 * @param code - The module code to check
 * @returns True if the module is enabled
 *
 * @example
 * ```tsx
 * function ExamButton() {
 *   const isEnabled = useIsModuleEnabled('exam');
 *
 *   if (!isEnabled) return null;
 *
 *   return <Button>Create Exam</Button>;
 * }
 * ```
 */
export function useIsModuleEnabled(code: ModuleCode): boolean {
  const { isModuleEnabled } = useModules();
  return isModuleEnabled(code);
}

/**
 * Hook to check if a feature flag is enabled.
 *
 * @param flag - The feature flag name
 * @returns True if the feature is enabled
 *
 * @example
 * ```tsx
 * function AiAnalyticsWidget() {
 *   const isEnabled = useIsFeatureEnabled('ai-analytics');
 *
 *   if (!isEnabled) return null;
 *
 *   return <AdvancedAnalytics />;
 * }
 * ```
 */
export function useIsFeatureEnabled(flag: string): boolean {
  const { isFeatureEnabled } = useModules();
  return isFeatureEnabled(flag);
}

/**
 * Hook to get navigation items from enabled modules.
 *
 * @param filter - Optional filter function
 * @returns Filtered and sorted navigation items
 *
 * @example
 * ```tsx
 * function MainNav() {
 *   const navItems = useNavItems((item) => item.requiresAuth !== false);
 *   return <Sidebar items={navItems} />;
 * }
 * ```
 */
export function useNavItems(
  filter?: (item: NavItem) => boolean
): NavItem[] {
  const { getNavItems } = useModules();
  const items = getNavItems();

  return useMemo(() => {
    if (!filter) return items;
    return items.filter(filter);
  }, [items, filter]);
}

/**
 * Hook to get routes from enabled modules.
 *
 * @returns Array of route configurations
 *
 * @example
 * ```tsx
 * function AppRoutes() {
 *   const routes = useRoutes();
 *
 *   return (
 *     <Routes>
 *       {routes.map((route) => (
 *         <Route key={route.path} {...route} />
 *       ))}
 *     </Routes>
 *   );
 * }
 * ```
 */
export function useRoutes(): RouteConfig[] {
  const { getRoutes } = useModules();
  return getRoutes();
}

/**
 * Hook to get dashboard widgets from enabled modules.
 *
 * @param userRole - Optional user role for filtering widgets
 * @returns Filtered and sorted widget configurations
 */
export function useDashboardWidgets(userRole?: string): DashboardWidgetConfig[] {
  const { getDashboardWidgets } = useModules();
  const widgets = getDashboardWidgets();

  return useMemo(() => {
    if (!userRole) return widgets;
    return widgets.filter(
      (w) => !w.requiredRoles || w.requiredRoles.includes(userRole)
    );
  }, [widgets, userRole]);
}

/**
 * Hook to get quick actions from enabled modules.
 *
 * @param userRole - Optional user role for filtering actions
 * @returns Filtered and sorted quick action configurations
 */
export function useQuickActions(userRole?: string): QuickActionConfig[] {
  const { getQuickActions } = useModules();
  const actions = getQuickActions();

  return useMemo(() => {
    if (!userRole) return actions;
    return actions.filter(
      (a) => !a.requiredRoles || a.requiredRoles.includes(userRole)
    );
  }, [actions, userRole]);
}

export { ModuleContext };
